import { MAINTENANCE, UNKNOWN_CODE } from "./codes";
import { parseRetryAfter } from "./retry-after";
import type { ApiFieldRefusal, ApiRefusal } from "./refusal";

type ResponseLike = { readonly status: number; readonly url?: string; readonly headers: { get(name: string): string | null } };

type ErrorBody = { code?: unknown; params?: unknown; fieldErrors?: unknown; traceId?: unknown; until?: unknown; overdue?: unknown; mode?: unknown; keepsWorkingKey?: unknown; note?: unknown };

function asBody(body: unknown): ErrorBody {
  return typeof body === "object" && body !== null ? (body as ErrorBody) : {};
}

function asParams(params: unknown): Record<string, unknown> {
  return typeof params === "object" && params !== null && !Array.isArray(params) ? { ...params } : {};
}

function asText(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function asFieldErrors(value: unknown): ApiFieldRefusal[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    const field = asText((entry as { field?: unknown } | null)?.field);
    const message = asText((entry as { message?: unknown } | null)?.message);
    return field && message ? [{ field, message }] : [];
  });
}

/** The edge's maintenance answer has no `params`: its few fields sit at the top of the body, so they are read as params. */
function maintenanceParams(body: ErrorBody): Record<string, unknown> {
  const { until, mode, keepsWorkingKey, note, overdue } = body;
  const texts = Object.fromEntries(Object.entries({ until, mode, keepsWorkingKey, note }).filter(([, value]) => asText(value) !== undefined));
  return typeof overdue === "boolean" ? { ...texts, overdue } : texts;
}

/** Builds the typed refusal from a failed response and its (possibly missing) JSON body. */
export function toRefusal(response: ResponseLike, body: unknown, now?: Date): ApiRefusal {
  const parsed = asBody(body);
  const retryAfter = parseRetryAfter(response.headers.get("Retry-After"), now);
  const traceId = asText(parsed.traceId);
  const code = asText(parsed.code) ?? UNKNOWN_CODE;
  const url = code === MAINTENANCE ? asText(response.url) : undefined;
  const fieldErrors = asFieldErrors(parsed.fieldErrors);
  return {
    status: response.status,
    code,
    params: code === MAINTENANCE ? maintenanceParams(parsed) : asParams(parsed.params),
    ...(retryAfter === undefined ? {} : { retryAfter }),
    ...(traceId === undefined ? {} : { traceId }),
    ...(url === undefined ? {} : { url }),
    ...(fieldErrors.length === 0 ? {} : { fieldErrors }),
  };
}
